import {
  Button,
  cn,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  HorizontalMenu,
  Select,
} from "@win-reality/react-ui";
import { CircleCheck } from "lucide-react";
import { useState } from "react";

import { TeamSide } from "../../constants/teams";
import {
  GameFullResponse,
  GamePlayerRef,
  LineUpPlayer,
} from "../../types/gameFull";
import { GameRosters } from "../../types/roster";
import { getBattingOrder } from "../../utils/lineupDraft";
import { formatPlayerName } from "../../utils/playerName";
import { TeamDisplay } from "../../utils/teamColors";
import { AddPlayersDialog } from "../Lineup/AddPlayersDialog";
import { PlayerName, RowShell, SlotBadge } from "../Lineup/PlayerRows";
import { canEditRoster, RosterEditTooltip } from "../Lineup/RosterEditTooltip";
import { ThemedDialog } from "../ThemedDialog";

export type PlayerChangeRole = "hitter" | "pitcher";
export type PlayerChangeTab = "change" | "subOut";

/** Which dialog is showing; only one is open at a time. */
type Step = "main" | "addPlayers";

type PlayerChangeDialogProps = {
  role: PlayerChangeRole;
  /** Pitchers only have Sub Out */
  initialTab: PlayerChangeTab;
  /** Team of the player being changed */
  side: TeamSide;
  /** That team's color */
  team: TeamDisplay;
  player: GamePlayerRef;
  data: GameFullResponse;
  rosters: GameRosters;
  onClose: () => void;
  /** Change Hitter: another hitter from the current lineup */
  onChangeHitter: (player: LineUpPlayer) => void;
  /** Sub Out: replaces the player in the lineup with a roster player */
  onSubOut: (player: LineUpPlayer) => void;
  /** Adds players to the roster; resolves to them with their saved ids */
  onAddRosterPlayers: (
    players: LineUpPlayer[],
  ) => Promise<LineUpPlayer[] | null>;
  onManageLineup: () => void;
  /** A change is saving: confirm buttons show loading */
  isSaving?: boolean;
};

/** Change Hitter: pick another hitter from the batting order. */
const ChangeHitterTab = ({
  team,
  battingOrder,
  selectedId,
  onSelect,
  onManageLineup,
}: {
  team: TeamDisplay;
  battingOrder: LineUpPlayer[];
  selectedId: string;
  onSelect: (playerId: string) => void;
  onManageLineup: () => void;
}) => (
  <div className="space-y-3">
    <p className="text-sm">Select another player from your current lineup</p>
    <div
      role="radiogroup"
      data-testid="plate-appearance-editor-player-list"
      className="space-y-2"
    >
      {battingOrder.map((p) => {
        const isSelected = p.playerId === selectedId;
        return (
          <Button
            key={p.playerId}
            variant="ghost"
            role="radio"
            aria-checked={isSelected}
            aria-current={isSelected ? "true" : undefined}
            data-testid="plate-appearance-editor-player-option"
            data-player-id={p.playerId}
            data-slot={p.battingOrder}
            onClick={() => onSelect(p.playerId)}
            className={cn(
              // Size
              "h-auto min-h-12 w-full",
              // Layout
              "justify-start gap-3",
              // Spacing
              "px-3 py-2",
              // Border
              "rounded-md border",
              isSelected
                ? "border-tagging-option-selected-border"
                : "border-tagging-option-border",
              // Background
              isSelected
                ? "bg-tagging-option-selected hover:bg-tagging-option-selected"
                : "bg-tagging-option",
              // Text
              "text-left font-regular normal-case",
            )}
          >
            <SlotBadge color={team.color} textColor={team.textColor}>
              {p.battingOrder}
            </SlotBadge>
            <PlayerName player={p} />
            {isSelected && (
              <CircleCheck className="h-5 w-5 shrink-0 text-tagging-accent" />
            )}
          </Button>
        );
      })}
    </div>
    <Button
      variant="ghost"
      size="xs"
      onClick={onManageLineup}
      data-testid="plate-appearance-editor-manage-lineup-button"
      className="px-0 font-semibold underline hover:no-underline"
    >
      Manage Lineup
    </Button>
  </div>
);

/** Sub Out: the outgoing player "for" a roster player picked from a select. */
const SubOutTab = ({
  team,
  roleLabel,
  slotLabel,
  outPlayer,
  options,
  selectedId,
  onSelect,
  roster,
  isRosterEditable,
  onAddPlayer,
}: {
  team: TeamDisplay;
  roleLabel: string;
  slotLabel: string | number;
  outPlayer: LineUpPlayer;
  options: LineUpPlayer[];
  selectedId: string | undefined;
  onSelect: (playerId: string) => void;
  roster: GameRosters[TeamSide];
  isRosterEditable: boolean;
  onAddPlayer: () => void;
}) => (
  <div className="space-y-4">
    <p className="text-sm">Sub out for another player</p>
    <RowShell>
      <SlotBadge color={team.color} textColor={team.textColor}>
        {slotLabel}
      </SlotBadge>
      <PlayerName player={outPlayer} />
    </RowShell>

    <div className="flex items-center gap-4 px-12 text-sm text-tagging-muted">
      <hr className="flex-1 border-tagging-option-border" />
      for
      <hr className="flex-1 border-tagging-option-border" />
    </div>

    {/* Slot badge sits inside the select's field */}
    <div className="relative [&_input]:!pl-12">
      <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2">
        <SlotBadge color={team.color} textColor={team.textColor}>
          {slotLabel}
        </SlotBadge>
      </div>
      <Select
        value={selectedId}
        onValueChange={onSelect}
        items={options.map((p) => ({
          value: p.playerId,
          label: p.jerseyNumber
            ? `#${p.jerseyNumber}  ${formatPlayerName(p.name)}`
            : formatPlayerName(p.name),
        }))}
        placeholder={`Select a new ${roleLabel}`}
      />
    </div>
    {options.length === 0 && (
      <p className="text-xs text-tagging-muted">
        {isRosterEditable
          ? "No other players on this roster. Add one below."
          : "No other players on this roster."}
      </p>
    )}

    <RosterEditTooltip roster={roster}>
      <Button
        variant="success-outline"
        size="sm"
        isDisabled={!isRosterEditable}
        onClick={onAddPlayer}
        data-testid="plate-appearance-editor-add-player-button"
        className="rounded-full"
      >
        + Add Player
      </Button>
    </RosterEditTooltip>
  </div>
);

/**
 * Active Hitter / Active Pitcher modal from the at-bat editor player cards:
 * Change Hitter (pick from the lineup) or Sub Out
 * (swap in a roster player or another player at this game).
 */
export const PlayerChangeDialog = ({
  role,
  initialTab,
  side,
  team,
  player,
  data,
  rosters,
  onClose,
  onChangeHitter,
  onSubOut,
  onAddRosterPlayers,
  onManageLineup,
  isSaving = false,
}: PlayerChangeDialogProps) => {
  const isHitter = role === "hitter";
  const [tab, setTab] = useState<PlayerChangeTab>(
    isHitter ? initialTab : "subOut",
  );
  const [step, setStep] = useState<Step>("main");
  const [changeId, setChangeId] = useState(player.playerId);
  const [subId, setSubId] = useState<string>();

  const roleLabel = isHitter ? "hitter" : "pitcher";
  const battingOrder = getBattingOrder(data.lineUp[side]);
  const lineupEntry = data.lineUp[side].find(
    (p) => p.playerId === player.playerId,
  );
  const outPlayer: LineUpPlayer = lineupEntry ?? {
    playerId: player.playerId,
    name: player.playerName,
  };
  // Hitters keep their batting order slot; pitchers show "P"
  const slotLabel = isHitter ? (lineupEntry?.battingOrder ?? "--") : "P";

  // Players new to the roster need the user to be able to edit it
  const isRosterEditable = canEditRoster(rosters[side]);
  // Hitters sub from the bench; pitchers from anyone else on the roster.
  // Other players at this game (not on a roster) can sub in too, when they
  // can join it.
  const subOptions = [
    ...rosters[side].players,
    ...(isRosterEditable
      ? data.lineUp.extra.filter(
          (p) => !rosters[side].players.some((r) => r.playerId === p.playerId),
        )
      : []),
  ].filter(
    (p) =>
      p.playerId !== player.playerId &&
      !(isHitter && battingOrder.some((b) => b.playerId === p.playerId)),
  );
  const changePlayer = battingOrder.find((p) => p.playerId === changeId);
  const subPlayer = subOptions.find((p) => p.playerId === subId);

  if (step === "addPlayers") {
    return (
      <AddPlayersDialog
        rosterPlayerIds={new Set(rosters[side].players.map((p) => p.playerId))}
        orgId={rosters[side].orgId}
        actionLabel="Add to Roster"
        onClose={() => setStep("main")}
        onAddPlayers={async (players) => {
          const added = await onAddRosterPlayers(players);
          // Pre-select the new player as the sub
          if (added?.[0]) setSubId(added[0].playerId);
        }}
      />
    );
  }

  return (
    <ThemedDialog
      open
      onOpenChange={(open) => !open && onClose()}
      defaultCancelButton={false}
      size="sm"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>
                {isHitter ? "Active Hitter" : "Active Pitcher"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 pb-2">
              {isHitter && (
                <HorizontalMenu
                  size="md"
                  items={[
                    {
                      label: "Change Hitter",
                      active: tab === "change",
                      onClick: () => setTab("change"),
                    },
                    {
                      label: "Sub Out",
                      active: tab === "subOut",
                      onClick: () => setTab("subOut"),
                    },
                  ]}
                  wrapperClassName="flex justify-center"
                  buttonClassName="text-gray-900 dark:text-white"
                />
              )}

              {tab === "change" ? (
                <ChangeHitterTab
                  team={team}
                  battingOrder={battingOrder}
                  selectedId={changeId}
                  onSelect={setChangeId}
                  onManageLineup={onManageLineup}
                />
              ) : (
                <SubOutTab
                  team={team}
                  roleLabel={roleLabel}
                  slotLabel={slotLabel}
                  outPlayer={outPlayer}
                  options={subOptions}
                  selectedId={subId}
                  onSelect={setSubId}
                  roster={rosters[side]}
                  isRosterEditable={isRosterEditable}
                  onAddPlayer={() => setStep("addPlayers")}
                />
              )}
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full items-center justify-between">
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="plate-appearance-editor-player-change-dialog-cancel-button"
                className="underline"
              >
                Cancel
              </Button>
              {tab === "change" ? (
                <Button
                  variant="secondary-flat-light"
                  isDisabled={
                    !changePlayer || changeId === player.playerId || isSaving
                  }
                  isLoading={isSaving}
                  aria-busy={isSaving}
                  onClick={() => changePlayer && onChangeHitter(changePlayer)}
                  data-testid="plate-appearance-editor-player-change-dialog-confirm-button"
                  data-value="change"
                  className="min-w-[136px] rounded-full"
                >
                  Change Hitter
                </Button>
              ) : (
                <Button
                  variant="secondary-flat-light"
                  isDisabled={!subPlayer || isSaving}
                  isLoading={isSaving}
                  aria-busy={isSaving}
                  onClick={() => subPlayer && onSubOut(subPlayer)}
                  data-testid="plate-appearance-editor-player-change-dialog-confirm-button"
                  data-value="subOut"
                  className="min-w-[136px] rounded-full"
                >
                  Sub Out
                </Button>
              )}
            </div>
          </DialogFooter>
        </>
      )}
    </ThemedDialog>
  );
};

export default PlayerChangeDialog;
